import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { generateId } from 'common/utils/string';
import React from 'react';

import {
  TourFlightAirlineFormType,
  TourFlightFormType,
  TourHotelDetailFormType,
  TourHotelFormType,
  TourListFormType,
  TourOtherPriceFormType,
  TourPriceFormType,
  TourProductFormType,
} from '../form-type';

export function onAddNewAirline(): TourFlightAirlineFormType {
  return {
    airlineId: '',
    customId: generateId(),
    date: null,
    detail: '',
    id: undefined,
    preferenceId: '',
    route: '',
    time: '',
  } as unknown as TourFlightAirlineFormType;
}

export function onAddNewFlight(): TourFlightFormType {
  return {
    customId: generateId(),
    id: undefined,
    name: '',
    note: '',
    refId: undefined,
    tourFlightAirlines: [onAddNewAirline()],
  };
}

export function onAddNewHotelDetail(): TourHotelDetailFormType {
  return {
    checkIn: null,
    checkOut: null,
    cityTax: '',
    customId: generateId(),
    description: '',
    hotelRoomId: '',
    id: undefined,
    night: undefined,
  } as unknown as TourHotelDetailFormType;
}

export function onAddNewHotel(): TourHotelFormType {
  return {
    customId: generateId(),
    details: [onAddNewHotelDetail()],
    hotelId: '',
    id: undefined,
    note: '',
    refId: undefined,
  };
}

export function onAddNewPrice(fieldLength?: number): TourPriceFormType {
  const isDefault = fieldLength !== undefined ? fieldLength === 0 : true;
  return {
    customId: generateId(),
    isDefault,
    id: undefined,
    price: undefined,
    refId: undefined,
    type: '',
  } as unknown as TourPriceFormType;
}

export function onAddNewOtherPrice(): TourOtherPriceFormType {
  return {
    id: undefined,
    customId: generateId(),
    description: '',
    price: undefined,
    refId: '',
  } as unknown as TourOtherPriceFormType;
}

export function onAddNewProduct(): TourProductFormType {
  return {
    id: undefined,
    customId: generateId(),
    refId: undefined,
    description: '',
    price: undefined,
    productVariantId: undefined,
    stock: undefined,
    qty: undefined,
    avgCogs: undefined,
  } as unknown as TourProductFormType;
}

interface UseGetDefaultValuesProps {
  tourList?: TourModel;
  defaultValues?: TourListFormType;
}

export default function useGetDefaultValues(props: UseGetDefaultValuesProps) {
  const { tourList } = props;

  const defaultValues = React.useMemo<TourListFormType>(() => {
    const transactionAt = tourList?.transactionAt ?? new Date();
    if (props.defaultValues) {
      return {
        ...props.defaultValues,
        transactionAt,
        tempTransactionAt: transactionAt,
        tourFlights: props.defaultValues.tourFlights.map((tf) => {
          return {
            ...tf,
            id: undefined,
            refId: undefined,
            tourFlightAirlines: tf.tourFlightAirlines.map((tfa) => {
              return {
                ...tfa,
                id: undefined,
              };
            }),
          };
        }),
        tourHotels: props.defaultValues.tourHotels.map((th) => {
          return {
            ...th,
            id: undefined,
            refId: undefined,
            details: th.details.map((detail) => {
              return {
                ...detail,
                id: undefined,
              };
            }),
          };
        }),
        tourPrices: props.defaultValues.tourPrices.map((tp) => {
          return {
            ...tp,
            id: undefined,
            refId: undefined,
          };
        }),
        tourOtherPrices: props.defaultValues.tourOtherPrices.map((top) => {
          return {
            ...top,
            id: undefined,
            refId: undefined,
          };
        }),
        tourProducts: props.defaultValues.tourProducts.map((tp) => {
          return {
            ...tp,
            id: undefined,
            refId: undefined,
          };
        }),
        refId: undefined,
        tourList: undefined,
      };
    }

    return {
      refId: tourList?.refId ?? undefined,
      adminCommission: tourList?.adminCommission ?? 0,
      code: tourList?.code || '',
      day: tourList?.day || 1,
      description: tourList?.description || '',
      employeeId: tourList?.admin?.id || '',
      salesmanId: tourList?.salesman?.id || '',
      operatorId: tourList?.operator?.id || '',
      endedAt: tourList?.endedAt || new Date(),
      startedAt: tourList?.startedAt || new Date(),
      name: tourList?.name || '',
      needApproval: false,
      operatorCommission: tourList?.operatorCommission || 0,
      salesCommission: tourList?.salesCommission || 0,
      tourCategoryId: tourList?.category?.id || '',
      transactionAt,
      tempTransactionAt: transactionAt,
      type: tourList?.type || 'private',
      // array
      tourFlights:
        tourList?.tourFlights?.map((tourFlight) => {
          const tourFlightAirlines: TourFlightAirlineFormType[] =
            tourFlight.tourFlightAirlines.map((tfa) => {
              return {
                airlineId: tfa.airline.id,
                customId: generateId(),
                date: tfa.date || new Date(),
                detail: tfa.detail || '',
                preferenceId: tfa.preference?.id || '',
                route: tfa.route || '',
                time: tfa.time || '',
                id: tfa.id,
              };
            });
          return {
            id: tourFlight.id,
            customId: generateId(),
            name: tourFlight.name,
            note: tourFlight.note || '',
            refId: tourFlight.refId,
            tourFlightAirlines,
          };
        }) ?? [],
      tourHotels:
        tourList?.tourHotels?.map((tourHotel) => {
          const details: TourHotelDetailFormType[] = tourHotel.details.map(
            (detail) => {
              return {
                checkIn: detail.checkIn,
                checkOut: detail.checkOut,
                cityTax: detail.cityTax || '',
                customId: generateId(),
                description: detail.description || '',
                hotelRoomId: detail.hotelRoom.id,
                night: detail.night,
                id: detail.id,
              };
            },
          );
          return {
            id: tourHotel.id,
            customId: generateId(),
            hotelId: tourHotel.hotel.id,
            note: tourHotel.note || '',
            refId: tourHotel.refId,
            details,
          };
        }) ?? [],
      tourPrices: tourList?.tourPrices?.map((tourPrice) => {
        return {
          id: tourPrice.id,
          customId: generateId(),
          isDefault: tourPrice.isDefault ?? tourList?.tourPrices.length === 1,
          price: tourPrice.price,
          type: tourPrice.type || '',
          refId: tourPrice.refId,
        };
      }) ?? [onAddNewPrice()],
      tourOtherPrices:
        tourList?.tourOtherPrices?.map((tourOtherPrice) => {
          return {
            id: tourOtherPrice.id,
            customId: generateId(),
            description: tourOtherPrice.description || '',
            price: tourOtherPrice.price,
            refId: tourOtherPrice.refId,
          };
        }) ?? [],
      tourProducts:
        tourList?.tourProducts?.map((tourProduct) => {
          return {
            id: tourProduct.id,
            customId: generateId(),
            refId: tourProduct.refId,
            description: tourProduct.description || '',
            price: tourProduct.productVariant.sellPrice,
            productVariantId: {
              id: tourProduct.productVariant.id,
              label: tourProduct.completeName,
            },
            stock: tourProduct.productVariant.stock,
            qty: tourProduct.qty,
            avgCogs: tourProduct.qty * tourProduct.productVariant.sellPrice,
          };
        }) ?? [],

      tourList,
    };
  }, [props.defaultValues, tourList]);

  return defaultValues;
}
